import { useEffect, useRef, useCallback, useState } from "react";

import {
  getGenerationJob,
  type GenerationJobDto,
} from "@/common/services/test-gen/test-gen.api";
import EnvConfig from "@/config/env";

/**
 * The persisted generation-job row (shared MySQL, replica-independent) plus its
 * result payload. The DB-poll fallback below hands this to consumers so an OPEN
 * dialog can recover progress + the final result even when the live SSE stream
 * is being served by a different backend replica than the one running the job.
 */
export type RecoveredGenerationJob = GenerationJobDto & { result?: unknown };

export type AiStreamEvent =
  | { type: "token"; data: { content: string; contextLabel?: string } }
  | { type: "agent_reasoning"; data: { thought: string } }
  | { type: "tool_start"; data: { tool: string; input: string } }
  | { type: "tool_end"; data: { tool: string; output: string } }
  | { type: "tool_error"; data: { tool: string; error: string } }
  | { type: "status"; data: { message: string; progress: number } }
  | { type: "update"; data: { type: string; data: any; generationId: string } }
  | {
      type: "complete";
      data: {
        message: string;
        data: any;
        progress: number;
        generationId: string;
      };
    }
  | { type: "error"; data: { message: string; generationId: string } };

type UseAiStreamSseOptions = {
  generationId: string | null;
  onStatusChange?: (message: string, progress: number) => void;
  onUpdate?: (
    type: string,
    data: any,
    generationId: string,
    meta: Record<string, any>,
  ) => void;
  onComplete?: (data: any, generationId: string) => void;
  onError?: (message: string, generationId: string) => void;
  /**
   * Durable recovery override (multi-replica safety net). When the live SSE
   * stream is silent — e.g. the generation POST and this EventSource GET landed
   * on different backend replicas, so the in-memory stream never delivers — a
   * background poll of the shared generation-job row detects terminal
   * completion and calls this with the full persisted job (incl. `result`).
   * Consumers whose `complete` payload is flow-specific (crawl/automation) wire
   * this to their own "render the final result" path. If omitted, the poll
   * falls back to `onComplete(job.result, id)`, which the other consumers
   * (they defensively accept the result array directly) already handle.
   */
  onRecoveredComplete?: (job: RecoveredGenerationJob) => void;
  /** Disable the DB-poll fallback (default: enabled). */
  pollFallback?: boolean;
};

export function useAiStreamSse({
  generationId,
  onStatusChange,
  onUpdate,
  onComplete,
  onError,
  onRecoveredComplete,
  pollFallback = true,
}: UseAiStreamSseOptions) {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const reconnectAttempts = useRef(0);
  // Tracks whether the stream has reached a terminal state (a `complete` event or
  // a genuine server-sent `error`). Once settled, the generic dataless `error`
  // event the browser fires on the normal end-of-stream close must be ignored —
  // otherwise we'd reconnect needlessly or flash a bogus "SSE error occurred".
  const settledRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY_MS = 3000;

  // ── DB-poll fallback tuning (multi-replica safety net) ──────────────────────
  const POLL_INITIAL_DELAY_MS = 4000; // let the live SSE get first crack
  const POLL_INTERVAL_MS = 4000;
  // Only take over terminal delivery once the live stream has been silent this
  // long, so a healthy same-replica SSE always owns `complete`/`error`.
  const SSE_SILENCE_MS = 10000;
  // Last-resort backstop: once the live SSE has given up (fast reconnects
  // exhausted), allow this many ms with NO forward progress AND no terminal row
  // before surfacing a soft "connection lost" error. Set generously so a
  // legitimately long/quiet generation is never falsely failed — it only bites a
  // genuinely dead job (its replica crashed before writing a terminal outcome).
  const STALL_AFTER_SSE_GIVEUP_MS = 150000; // 2.5 min

  const [isConnected, setIsConnected] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState<Record<string, string>>(
    {},
  );

  // Live mirrors for use inside the poll closure without re-subscribing.
  const progressRef = useRef(0);
  progressRef.current = progress;
  // Wall-clock of the last SSE *data* event (status/update/token/complete). Only
  // the replica actually running the job emits these, so a stale value means our
  // EventSource is pinned to a foreign/dead replica and the poll should take over.
  const lastSseDataAtRef = useRef(0);
  // Set true once the fast SSE reconnects are exhausted. From that point the live
  // stream is gone (likely pinned to a foreign/rotated replica) and the durable
  // DB-poll below is the ONLY delivery path — so it must NOT be torn down by a
  // spurious "connection lost" error. The poll owns terminal delivery from here.
  const sseGaveUpRef = useRef(false);
  // Wall-clock of the last FORWARD progress (SSE status or a poll advance). Drives
  // the last-resort stall backstop so a genuinely dead job (e.g. the replica
  // running it crashed before writing a terminal row) still surfaces an error
  // instead of spinning forever — without the old ~15s false "lost" alarm.
  const lastProgressAtRef = useRef(0);
  // Keep the latest pollFallback flag readable inside the stable `connect`
  // callback without adding it to the dep array (it's constant per consumer).
  const pollFallbackRef = useRef(pollFallback);
  pollFallbackRef.current = pollFallback;

  // Stable callback refs so they don't trigger re-connects
  const callbacksRef = useRef({
    onStatusChange,
    onUpdate,
    onComplete,
    onError,
    onRecoveredComplete,
  });
  callbacksRef.current = {
    onStatusChange,
    onUpdate,
    onComplete,
    onError,
    onRecoveredComplete,
  };

  const connect = useCallback(() => {
    if (!generationId) return;

    if (
      eventSourceRef.current &&
      eventSourceRef.current.readyState !== EventSource.CLOSED
    ) {
      return;
    }

    const sseUrl = `${EnvConfig.serverURL}/api/sse/ai-stream?generationId=${encodeURIComponent(generationId)}`;

    try {
      const es = new EventSource(sseUrl, { withCredentials: true });
      eventSourceRef.current = es;

      // Any real data event proves this EventSource is pinned to the replica
      // running the job — keeps the DB-poll fallback passive while SSE is healthy.
      const markSseData = () => {
        lastSseDataAtRef.current = Date.now();
      };

      es.addEventListener("connected", () => {
        reconnectAttempts.current = 0;
        setIsConnected(true);
      });

      es.addEventListener("token", (event) => {
        markSseData();
        try {
          const payload = JSON.parse(event.data);
          const label = payload.contextLabel || "__default__";
          setStreamingText((prev) => ({
            ...prev,
            [label]: (prev[label] || "") + (payload.content || ""),
          }));
        } catch {
          // ignore malformed token
        }
      });

      es.addEventListener("agent_reasoning", (event) => {
        try {
          const payload = JSON.parse(event.data);
          const label = "__reasoning__";
          setStreamingText((prev) => ({
            ...prev,
            [label]: (prev[label] || "") + (payload.thought || "") + "\n",
          }));
        } catch {
          // ignore
        }
      });

      es.addEventListener("tool_start", (event) => {
        try {
          const payload = JSON.parse(event.data);
          const label = `__tool__${payload.tool}`;
          setStreamingText((prev) => ({
            ...prev,
            [label]: `Running ${payload.tool}…\nInput: ${payload.input || ""}\n`,
          }));
        } catch {
          // ignore
        }
      });

      es.addEventListener("tool_end", (event) => {
        try {
          const payload = JSON.parse(event.data);
          const label = `__tool__${payload.tool}`;
          setStreamingText((prev) => ({
            ...prev,
            [label]:
              (prev[label] || "") + `\nOutput: ${payload.output || ""}\n`,
          }));
        } catch {
          // ignore
        }
      });

      es.addEventListener("tool_error", (event) => {
        try {
          const payload = JSON.parse(event.data);
          const label = `__tool__${payload.tool}`;
          setStreamingText((prev) => ({
            ...prev,
            [label]: (prev[label] || "") + `\nError: ${payload.error || ""}\n`,
          }));
        } catch {
          // ignore
        }
      });

      es.addEventListener("status", (event) => {
        markSseData();
        try {
          const payload = JSON.parse(event.data);
          setProgress(payload.progress || 0);
          setStatusMessage(payload.message || null);
          lastProgressAtRef.current = Date.now();
          callbacksRef.current.onStatusChange?.(
            payload.message,
            payload.progress,
          );
        } catch {
          // ignore
        }
      });

      es.addEventListener("update", (event) => {
        markSseData();
        try {
          const {
            type,
            data,
            generationId: gid,
            ...meta
          } = JSON.parse(event.data);
          callbacksRef.current.onUpdate?.(type, data, gid, meta);
        } catch {
          // ignore
        }
      });

      es.addEventListener("complete", (event) => {
        markSseData();
        settledRef.current = true;
        try {
          const payload = JSON.parse(event.data);
          setProgress(payload.progress || 100);
          setStatusMessage(payload.message || "Complete");
          callbacksRef.current.onComplete?.(
            payload.data ?? payload,
            payload.generationId,
          );
        } catch {
          setProgress(100);
          setStatusMessage("Complete");
          callbacksRef.current.onComplete?.(null, generationId ?? "");
        }
        es.close();
        eventSourceRef.current = null;
        setIsConnected(false);
      });

      es.addEventListener("error", (event) => {
        const msgEvent = event as MessageEvent;
        // The browser dispatches a *dataless* generic `error` event whenever the
        // connection drops or the server closes the stream — including the normal
        // close right after `complete`. That is NOT a server-reported failure, so
        // don't surface it here; `es.onerror` owns reconnect/close handling and
        // the backend replays the buffered `complete` on reconnect. Only a real
        // server-sent `error` event carries a JSON payload in `.data`.
        if (!msgEvent.data) return;
        settledRef.current = true;
        try {
          const payload = JSON.parse(msgEvent.data);
          setStatusMessage(payload.message || "Error");
          callbacksRef.current.onError?.(payload.message, payload.generationId);
        } catch {
          setStatusMessage("SSE error occurred");
          callbacksRef.current.onError?.(
            "SSE error occurred",
            generationId ?? "",
          );
        }
        es.close();
        eventSourceRef.current = null;
        setIsConnected(false);
      });

      es.onerror = () => {
        es.close();
        eventSourceRef.current = null;
        setIsConnected(false);

        // Stream already finished (a `complete` event or a server-sent `error`).
        // The generic error the browser fires on the normal end-of-stream close is
        // expected — don't reconnect or surface a late error.
        if (settledRef.current) return;

        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
        } else if (pollFallbackRef.current === false) {
          // No durable fallback for this consumer — surface the terminal error so
          // it can reset instead of hanging silently.
          callbacksRef.current.onError?.(
            "Connection lost. Please try generating again.",
            generationId ?? "",
          );
        } else {
          // Durable DB-poll fallback IS active — do NOT surface a fatal error.
          // The live SSE is gone (the generation's POST and this EventSource
          // likely landed on different backend replicas, or the replica rotated),
          // but the shared ai_generation_jobs row is the authoritative source and
          // the poll below delivers progress + the terminal outcome regardless of
          // replica routing. Firing onError here is exactly what produced the rare
          // "connection lost / already generated on reopen" glitch: the consumer's
          // error path reset the flow and tore down this very poll before it could
          // recover. Hand off to the poll instead; `sseGaveUp` lets it apply its
          // own generous stall backstop so a genuinely dead job still errors out.
          sseGaveUpRef.current = true;
          lastProgressAtRef.current = Date.now();
          console.warn(
            `[useAiStreamSse] SSE reconnects exhausted for ${generationId}; ` +
              "handing off to the durable DB-poll fallback (no user-facing error).",
          );
        }
      };
    } catch {
      // silently fail — caller can rely on polling if needed
    }
  }, [generationId]);

  useEffect(() => {
    if (generationId) {
      reconnectAttempts.current = 0;
      settledRef.current = false;
      sseGaveUpRef.current = false;
      // Start the SSE-silence clock now so the poll gives the live stream a fair
      // grace window before taking over on a foreign-replica connection.
      lastSseDataAtRef.current = Date.now();
      lastProgressAtRef.current = Date.now();
      progressRef.current = 0;
      setStreamingText({});
      setProgress(0);
      setStatusMessage(null);
      connect();
    }
  }, [generationId, connect]);

  // ── Durable DB-poll fallback (multi-replica safety net) ─────────────────────
  // SSE state is in-memory per backend replica. In production the API scales to
  // several replicas with no shared bus, so the generation POST and this
  // EventSource GET can land on different replicas — the live stream then never
  // delivers and the dialog freezes ("[pushSseEvent] No active connections").
  // Every generation also persists progress/status/result to a shared DB row
  // (ai_generation_jobs). Polling that row guarantees progress, terminal errors,
  // and the final result ALWAYS reach the user regardless of replica routing,
  // scale-in/out, or a dropped SSE — with no new infrastructure. On the healthy
  // same-replica path the poll stays passive (progress is a monotonic no-op and
  // terminal delivery is gated behind SSE silence, so the live stream owns it).
  useEffect(() => {
    if (!generationId || pollFallback === false) return;

    let cancelled = false;
    let inFlight = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const closeStream = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
    };

    const tick = async () => {
      if (cancelled || settledRef.current || inFlight) return;
      inFlight = true;
      try {
        const res = await getGenerationJob(generationId);
        const job = res?.data?.job as RecoveredGenerationJob | undefined;
        if (!job || cancelled || settledRef.current) return;

        // Always safe: advance the bar to match the shared row. Monotonic, and
        // never ahead of a live SSE (the DB row is written throttled from the
        // same replica), so this is a no-op on the healthy same-replica path.
        if (
          typeof job.progress === "number" &&
          job.progress > progressRef.current
        ) {
          progressRef.current = job.progress;
          setProgress(job.progress);
          if (job.message) setStatusMessage(job.message);
          callbacksRef.current.onStatusChange?.(
            job.message ?? "",
            job.progress,
          );
          lastProgressAtRef.current = Date.now();
        }

        // Terminal recovery only once the live stream has gone quiet, so a
        // healthy same-replica SSE always owns the flow-specific `complete`.
        if (Date.now() - lastSseDataAtRef.current < SSE_SILENCE_MS) return;

        if (job.status === "completed") {
          settledRef.current = true;
          progressRef.current = 100;
          setProgress(100);
          setStatusMessage(job.message ?? "Complete");
          if (callbacksRef.current.onRecoveredComplete) {
            callbacksRef.current.onRecoveredComplete(job);
          } else {
            // The other consumers defensively accept the persisted result array.
            callbacksRef.current.onComplete?.(job.result ?? null, generationId);
          }
          stopPolling();
          closeStream();
        } else if (job.status === "failed" || job.status === "cancelled") {
          settledRef.current = true;
          // Keep the word "cancelled" in the message so consumer onError handlers
          // route a user-cancel to their cancellation path rather than an error toast.
          const fallbackMsg =
            job.status === "cancelled"
              ? "Generation cancelled"
              : "Generation failed";
          callbacksRef.current.onError?.(
            job.error ?? fallbackMsg,
            generationId,
          );
          stopPolling();
          closeStream();
        } else if (
          // Still "running", but the live SSE has given up AND the shared row has
          // not advanced for a long time → the job is genuinely stuck (its replica
          // most likely crashed before writing a terminal outcome). Surface a soft
          // error as a last resort so the user can retry rather than spin forever.
          sseGaveUpRef.current &&
          Date.now() - lastProgressAtRef.current > STALL_AFTER_SSE_GIVEUP_MS
        ) {
          settledRef.current = true;
          callbacksRef.current.onError?.(
            "Connection lost. Please try generating again.",
            generationId,
          );
          stopPolling();
          closeStream();
        }
      } catch {
        // 404 = job row not yet committed (POST returns 202 before startJob) or a
        // transient network hiccup — keep polling; never surface as a failure.
        // BUT if the live SSE has already given up and the row has been unreachable
        // with no forward progress for the full stall window, a hard outage is in
        // play — settle with a soft error so the dialog doesn't hang indefinitely.
        if (
          sseGaveUpRef.current &&
          !settledRef.current &&
          Date.now() - lastProgressAtRef.current > STALL_AFTER_SSE_GIVEUP_MS
        ) {
          settledRef.current = true;
          callbacksRef.current.onError?.(
            "Connection lost. Please try generating again.",
            generationId,
          );
          stopPolling();
          closeStream();
        }
      } finally {
        inFlight = false;
      }
    };

    const startTimer = setTimeout(() => {
      void tick();
      intervalId = setInterval(() => void tick(), POLL_INTERVAL_MS);
    }, POLL_INITIAL_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      stopPolling();
    };
  }, [generationId, pollFallback]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setIsConnected(false);
    };
  }, [generationId]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  return { isConnected, progress, statusMessage, streamingText, disconnect };
}
